/**
 * Parser for the BE-emitted `X-Deprecated-Fields` response header
 * (studio-series-courses-api-contract-v3.md §17.1).
 *
 * Grammar:
 *   Header value is a comma-separated list of entries.
 *   Each entry: `<field> reason="<short>" sunset=<marker>` (space-separated).
 *
 * Example:
 *   X-Deprecated-Fields: series_id reason="use POST /api/studio/series/{uuid}/videos/{video:uuid}" sunset=PR7
 *
 * Multi-entry example:
 *   series_id reason="..." sunset=PR7, legacy_flag reason="..." sunset=2026-Q4
 *
 * The parser is generic — no `series_id`-specific logic. Any field BE
 * declares deprecated in any future contract is surfaced through the
 * same machinery.
 */

export interface DeprecatedField {
  field: string;
  reason: string;
  sunset: string;
}

const FIELD_RE = /^([a-z_][a-z0-9_]*)/i;
const REASON_RE = /reason="([^"]*)"/;
const SUNSET_RE = /sunset=(\S+)/;

export function parseDeprecatedFields(header: string | null | undefined): DeprecatedField[] {
  if (!header) return [];
  const seen = new Set<string>();
  return header
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((entry) => {
      const field = entry.match(FIELD_RE)?.[1];
      if (!field) return null;
      return {
        field,
        reason: entry.match(REASON_RE)?.[1] ?? '',
        sunset: entry.match(SUNSET_RE)?.[1] ?? '',
      };
    })
    .filter((d): d is DeprecatedField => {
      if (!d) return false;
      if (seen.has(d.field)) return false;
      seen.add(d.field);
      return true;
    });
}
